<template>
  <v-container
    class="pa-0"
    fluid>
    <v-form
      ref="product-form"
      @submit.prevent="onSave()">
      <div class="product-toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
        <span v-if="!isEditProduct">
          Create Product
        </span>
        <span v-else> Update : {{ formData.name }}</span>
        <div
          class="d-flex flex-row align-center"
          style="gap: 0.625rem;">
          <v-btn
            v-if="isEditProduct && isRemovable"
            color="error"
            outlined
            rounded
            small
            :loading="loading"
            @click="confirmDeleteProduct()">
            {{ $t('button.Remove') }}
          </v-btn>
          <v-btn
            color="success"
            rounded
            small
            :loading="loading"
            type="submit"
          >
            {{ $t('button.Save') }}
          </v-btn>
        </div>
      </div>
      <div class="row pa-0 ma-0">
        <v-col
          v-if="!isEditProduct"
          cols="12"
          class="px-2"
          md="6">
          <ProductSelector @select="onSelectProduct" />
        </v-col>
        <v-col
          cols="12"
          :class="{ 'col-md-6': !isEditProduct }"
          class="product-form px-2"
        >
          <v-col
            v-if="!isEditProduct"
            class="card-container elevation-3"
            cols="12"
            :class="{ 'card-container-error': isProductCategoryNotMatch }">
            <v-row>
              <v-col
                cols="12">
                <div
                  class="card-title"
                  :class="{ 'text-red': isProductCategoryNotMatch }">
                  Selected Product
                </div>
              </v-col>
              <v-col
                v-for="(product, index) in selectedProducts"
                :key="`product-${index}`"
                cols="12">
                <ProductCard
                  v-bind="product"
                  remove-able
                  @remove="selectedProducts.splice(index, 1)" />
              </v-col>
              <v-col
                v-if="isProductCategoryNotMatch"
                class="text-red">
                <span>Category ของสินค้าไม่ตรงกัน / Product categories do not match.</span>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            class="card-container elevation-3 mt-4"
            cols="12">
            <v-row no-gutters>
              <v-col cols="12">
                <div class="card-title">
                  Basic Information
                </div>
              </v-col>
              <v-col
                cols="12"
                class="text-bold mt-2">
                Product Images
              </v-col>
              <v-col
                cols="12"
                class="mt-2 mb-6">
                <gw-drop-file
                  v-model="formData.images.image"
                  accept="image/*"
                  :loading="loading"
                  :img-aspect-ratio="0.75"
                  s3-path="/lazada-product-images"
                  multiple
                  :limit="8"
                  required />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <v-text-field
                  v-model="formData.name"
                  label="Product title"
                  :rules="[
                    ...rules.required,
                    ...rules.minLength(1),
                    ...rules.maxLength(255)
                  ]"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <CategoriesTree
                  v-model="formData.primaryCategory"
                  :rules="rules.required"
                  :recommend-id="recommendCategoryId"
                  :disabled="isEditProduct || loading" />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <v-autocomplete
                  v-model="formData.brandId"
                  :disabled="isEditProduct"
                  :items="brands"
                  item-text="name"
                  item-value="id"
                  label="Brand"
                  :rules="rules.required"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <AttributesForm
                  v-model="formData.attributes"
                  :attributes="attributes"
                  :loading="loading" />
              </v-col>
            </v-row>
          </v-col>
          <v-col
            class="card-container elevation-3 mt-4"
            cols="12">
            <div class="title">
              Product details
            </div>
            <div class="mt-4">
              <VueEditor
                v-model="formData.description"
                :editor-toolbar="customToolbar">
              </VueEditor>
              <v-text-field
                :value="formData.description"
                class="hidden-field"
                label="Product description"
                :rules="[
                  ...rules.required,
                  ...rules.maxLength(25000)
                ]" />
            </div>
          </v-col>
          <v-col
            class="card-container elevation-1 mt-4"
            cols="12">
            <div class="title">
              Sales information
            </div>
            <div
              v-if="skus && skus.length !== 0"
              class="mt-4">
              <v-simple-table class="elevation-1">
                <thead>
                  <tr>
                    <th class="text-center">
                      Image
                    </th>
                    <th class="text-center">
                      Seller SKU
                    </th>
                    <th
                      v-for="col in variantColumns"
                      :key="col.name"
                      class="text-center">
                      {{ col.label }}
                    </th>
                    <th class="text-right">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(sku, skuIndex) in skus"
                    :key="`sku-${skuIndex}`">
                    <td
                      class="py-1"
                      width="200px">
                      <gw-drop-file
                        :loading="loading"
                        accept="image/*"
                        caption="Click to upload"
                        s3-path="/lazada-product-sku"
                        :value="sku.images.image"
                        :limit="1"
                        required
                        @input="(event) => {
                          sku.images.image = event;
                        }" />
                    </td>
                    <td class="text-center">
                      <div class="d-flex flex-column align-center justify-center">
                        <span>
                          {{ sku.sellerSku }}
                        </span>
                        <span class="uppercase">
                          {{ `${sku.gwSku.color} - ${sku.gwSku.size}` }}
                        </span>
                      </div>
                    </td>
                    <td
                      v-for="col in variantColumns"
                      :key="col.name"
                      class="text-center"
                      width="200px">
                      <v-text-field
                        v-if="!col.options.length"
                        :value="getSaleProp(sku, col.name)"
                        :disabled="isEditProduct"
                        :rules="[
                          ...rules.required,
                          () => isDuplicateOption(sku)
                        ]"
                        :label="col.label"
                        dense
                        outlined
                        hide-details="auto"
                        @input="(value) => setSaleProp(sku, col.name, value)" />
                      <v-autocomplete
                        v-else
                        :value="getSaleProp(sku, col.name)"
                        :items="col.options"
                        item-text="name"
                        item-value="name"
                        :label="col.label"
                        :return-object="false"
                        :disabled="isEditProduct"
                        :rules="[
                          ...rules.required,
                          () => isDuplicateOption(sku)
                        ]"
                        dense
                        outlined
                        hide-details="auto"
                        @input="(value) => setSaleProp(sku, col.name, value)" />
                    </td>
                    <td class="text-right">
                      {{ sku.price | showFullPriceFormat() }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-col>
          <v-col
            v-if="!isEditProduct"
            class="card-container elevation-3 mt-4"
            cols="12">
            <div class="title">
              Size Chart <span
                v-if="isSizeChartRequired"
                class="error--text">*</span>
            </div>
            <div class="mt-4">
              <gw-drop-file
                v-model="sizeChartImage"
                :loading="loading"
                :required="isSizeChartRequired"
                accept="image/*"
                s3-path="/lazada-product-size-charts"
                :limit="1" />
            </div>
          </v-col>
          <v-col class="card-container elevation-3 mt-4">
            <div class="title">
              Shipping Details
            </div>
            <div class="text-bold mt-8">
              Package dimension
            </div>
            <v-row class="mt-1">
              <v-col
                cols="12"
                md="4">
                <v-text-field
                  v-model.number="packageWidth"
                  type="number"
                  label="Width"
                  :suffix="units.CENTIMETER"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                md="4">
                <v-text-field
                  v-model.number="packageHeight"
                  type="number"
                  label="Height"
                  :suffix="units.CENTIMETER"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                md="4">
                <v-text-field
                  v-model.number="packageLength"
                  type="number"
                  label="Length"
                  :suffix="units.CENTIMETER"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
            </v-row>
            <div class="text-bold mt-8">
              Package weight
            </div>
            <v-row class="mt-1">
              <v-col cols="12">
                <v-text-field
                  v-model.number="packageWeight"
                  type="number"
                  label="Weight"
                  :suffix="units.GRAM"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
            </v-row>
          </v-col>
        </v-col>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { VueEditor } from 'vue2-editor'
import humps from 'humps'
import LazadaProvider from '@/resources/LazadaProvider.js'
import CategoriesTree from '../components/CategoriesTree.vue'
import AttributesForm from '../components/AttributesForm.vue'
import ProductSelector from '../components/ProductSelector.vue'
import ProductCard from '../components/ProductCard.vue'
import SizeValues from '@/assets/js/SizeValues'
import MatchLazadaOption from '@/assets/js/MatchLazadaOption'
import { getRole } from '@/assets/js/Authentication'
// import LazadaAttributes from '@/assets/json/Mockup/LazadaAttributes.json'

const LazadaService = new LazadaProvider()

// System size values that map to a differently-named Lazada size option.
// Keyed by uppercased system size; value is matched against the Lazada options.
const SIZE_ALIASES = {
  F: 'One Size'
}

export default {
  components: {
    CategoriesTree,
    AttributesForm,
    ProductCard,
    ProductSelector,
    VueEditor
  },
  data () {
    return {
      loading: false,
      allowRoleRemoveProduct: [
        'management',
        'developer',
        'merchandising_planner',
        'merchandiser',
        'inventory_and_costing',
        'accounting_manager',
        'support'
      ],
      units: {
        CENTIMETER: 'CM',
        KILOGRAM: 'KG',
        GRAM: 'GRAM'
      },
      attributes: [],
      brands: [
        // {
        //   id: '56645',
        //   name: 'Sandbox'
        // },
        {
          id: '110381676',
          name: 'Gentlewoman'
        }
      ],
      selectedProducts: [],
      recommendCategoryId: '',
      lazadaData: {},
      allImages: [],
      formData: {
        id: null,
        itemId: '',
        name: '',
        description: '',
        brandId: '',
        model: '',
        shortDescription: '',
        primaryCategory: '',
        associatedSku: '',
        images: {
          image: []
        },
        disableAutoFillAttribute: false,
        attributes: {},
        skus: {
          sku: []
        }
      },
      packageHeight: 0,
      packageLength: 0,
      packageWidth: 0,
      packageWeight: 0,
      packageContent: '',
      sizeChartImage: null,
      rules: {
        required: [
          (v) => !!v || 'This field is required.'
        ],
        number: [
          (v) => !!v || 'This field is required.',
          (v) => /^\d+(\.\d{1,2})?$/.test(v) || 'This field must be valid number.',
          // (v) => /^[+-]?\d+$/.test(v) || 'This field must be valid number.',
          (v) => v > 0 || 'This field must be positive number.'
        ],
        minLength: (length = 25) => ([
          (v) => (!!v && v.length >= length) || `This field must be at least ${length} characters`
        ]),
        maxLength: (length = 255) => ([
          (v) => (!!v && v.length <= length) || `This field must be at most ${length} characters`
        ])
      },
      customToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        // ['link', 'image'],
        ['clean']
      ],
      variantColumns: [],
      isSizeChartRequired: false
    }
  },
  computed: {
    isRemovable () {
      const role = getRole()

      return this.allowRoleRemoveProduct.includes(role)
    },
    isProductCategoryNotMatch () {
      const initialCategory = this.selectedProducts[0]?.productPrototype.category || null
      return this.selectedProducts.some((product) => product.productPrototype.category !== initialCategory)
    },
    brandName () {
      return this.brands.find((brand) => brand.id === this.formData.brandId)?.name || 'Gentlewoman'
    },
    isEditProduct () {
      return this.$route.name === 'LazadaEditProduct' && !!this.$route.params.productId
    },
    editProductId () {
      return this.$route.params.productId || null
    },
    skus () {
      if (this.isEditProduct) {
        const groupProducts = []
        for (const sku of this.formData.skus.sku) {
          const product = groupProducts.find((v) => v.id === sku.gwSku.productId)
          if (product) {
            product.skus.push(sku)
          } else {
            groupProducts.push({
              id: sku.gwSku.productId,
              skus: [sku]
            })
          }
        }
        return groupProducts.reduce(
          (acc, product) => [...acc, ...product.skus.sort((a, b) => SizeValues(a.gwSku.size) - SizeValues(b.gwSku.size))],
          []
        )
      }

      return this.selectedProducts
        .reduce((acc, product) => [
          ...acc,
          ...this.sortSize(product.skus).map((sku) => {
            const saleProps = this.variantColumns.map((col) => {
              if (col.source === 'color') {
                return { name: col.name, value: MatchLazadaOption(sku.color.name, col.options) }
              }
              if (col.source === 'size') {
                const aliasedSize = SIZE_ALIASES[String(sku.size.name).trim().toUpperCase()] || sku.size.name
                return { name: col.name, value: MatchLazadaOption(aliasedSize, col.options) }
              }
              return { name: col.name, value: '' }
            })

            return {
              saleProps,
              sellerSku: sku.code,
              price: sku.price,
              images: {
                image: sku.images
              },
              gwSku: {
                productId: product.id,
                skuId: sku.id,
                model: product.model,
                code: sku.code,
                color: sku.color.name,
                size: sku.size.name,
                price: sku.price
              }
            }
          })
        ], [])
    }
  },
  watch: {
    'selectedProducts': {
      handler () {
        if (this.selectedProducts.length === 1) {
          // get recommend cate
          this.getRecommendCategory()
        }
      },
      deep: true
    },
    'formData.primaryCategory': {
      handler () {
        this.getAttributeList()
      },
      deep: true
    }
  },
  mounted () {
    this.initialize()
  },
  methods: {
    ...mapActions({
      setErrorPage: 'Components/setErrorPage',
      setSnackbar: 'Components/setSnackbar'
    }),
    sortSize (skus) {
      return skus.sort((a, b) => SizeValues(a.size.name) - SizeValues(b.size.name))
    },
    detectSource (name) {
      if (name === 'colorFamily' || name === 'color_family') return 'color'
      if (name === 'size' || /size/i.test(name)) return 'size'
      return 'manual'
    },
    getSaleProp (sku, name) {
      return sku.saleProps?.find((prop) => prop.name === name)?.value ?? ''
    },
    setSaleProp (sku, name, value) {
      if (!sku.saleProps) this.$set(sku, 'saleProps', [])

      const found = sku.saleProps.find((prop) => prop.name === name)

      if (found) found.value = value
      else sku.saleProps.push({ name, value })
    },
    isDuplicateOption (sku) {
      const key = (s) => this.variantColumns.map((c) => String(this.getSaleProp(s, c.name)).toUpperCase()).join('|')
      const duplicateOption = this.skus.filter((sk) => key(sk) === key(sku))

      if (duplicateOption.length >= 2) return 'Duplicate Options'

      return true
    },
    async initialize () {
      this.loading = true
      try {
        if (this.isEditProduct) {
          await this.getProductDetail()
        }
      } catch (error) {
        this.setErrorPage({
          code: error.code,
          message: error.message
        })
        console.error('initialize', error)
      } finally {
        this.loading = false
      }
    },
    async getRecommendCategory () {
      if (this.loading === true) return

      try {
        this.loading = true
        const image = this.formData.images.image?.[0] || ''

        if (!image) return

        const { data } = await LazadaService.getRecommendCategoryList({
          brand: this.brandName,
          productName: this.formData.name,
          imageUrl: image
        })
        const categories = (data?.categorySuggestions || []).map((v) => ({ ...v, categoryId: String(v.categoryId) }))
        this.recommendCategoryId = categories[categories.length - 1]?.categoryId || null
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
    }
    },
    async getProductDetail () {
      const { data: { raw, data } } = await LazadaService.getProductDetail(this.editProductId, { brand: this.brandName })
      const description = (data.description || '')
        .replaceAll('</br>', '')
        .replaceAll('<br>', '<p><br></p>')
        .replaceAll('<div>', '<p>')
        .replaceAll('</div>', '</p>')

      this.formData = {
        id: data.itemId,
        ...data,
        description,
        brandId: data.brandId === 'GENTLEWOMAN' ? '110381676' : data.brandId,
        attributes: data.attributes.reduce((acc, attr) => {
          acc[attr.name] = attr.value
          return acc
        }, {})
      }

      const firstSku = data?.skus?.sku?.[0]

      this.packageHeight = firstSku?.packageHeight || 0
      this.packageLength = firstSku?.packageLength || 0
      this.packageWidth = firstSku?.packageWidth || 0
      this.packageWeight = (firstSku?.packageWeight || 0) * 1000
      this.packageContent = firstSku?.packageContent || ''

      this.lazadaData = raw
    },
    async getAttributeList () {
      try {
        const { data } = await LazadaService.getAttributeList({
          brand: this.brandName,
          categoryId: this.formData.primaryCategory
        })

        const rawAttributes = humps.camelizeKeys(data || []).filter(Boolean)
        const excludeNames = [
          'name',
          'price',
          'quantity',
          'brand',
          '__images__',
          'SellerSku',
          'package_content',
          'package_weight',
          'package_length',
          'package_width',
          'package_height',
          'package_contents_en',
          'short_description',
          'description',
          'description_en',
          'short_description_en',
          'size_chart',
          'sizeChart'
        ]

        this.isSizeChartRequired = rawAttributes.some((attribute) => attribute.name === 'size_chart' || attribute.name === 'sizeChart')

        if (this.isEditProduct) {
          const firstSku = this.formData.skus?.sku?.[0]
          this.variantColumns = (firstSku?.saleProps || []).map(({ name, label }) => {
            const attr = rawAttributes.find((a) => a.name === name) || {}
            return {
              name,
              label: label || attr.label || name,
              inputType: attr.inputType || 'text',
              options: attr.options || [],
              isMandatory: attr.isMandatory || false
            }
          })
        } else {
          this.variantColumns = rawAttributes
            .filter((attr) => attr.isSaleProp === 1)
            .sort((a, b) => Number(a.id) - Number(b.id))
            .sort((a, b) => Number(b.advanced.isKeyProp) - Number(a.advanced.isKeyProp))
            .sort((a, b) => Number(b.isMandatory) - Number(a.isMandatory))
            .slice(0, 2)
            .map((attr) => ({
              name: attr.name,
              label: attr.label || attr.name,
              inputType: attr.inputType,
              options: attr.options || [],
              isMandatory: attr.isMandatory,
              source: this.detectSource(attr.name)
            }))
        }

        this.attributes = rawAttributes
          .filter((attribute) => attribute.isSaleProp === 0
            && (attribute.isMandatory || attribute.advanced.isKeyProp)
            && !excludeNames.includes(attribute.name))
          .sort((a, b) => Number(a.id) - Number(b.id))
          .sort((a, b) => Number(b.advanced.isKeyProp) - Number(a.advanced.isKeyProp))
          .sort((a, b) => Number(b.isMandatory) - Number(a.isMandatory))

        const newAttributeValues = {}
        this.attributes.forEach((attr) => {
          const existingValue = this.formData.attributes?.[attr.name]
          const isMulti = ['multiSelect', 'multiEnumInput'].includes(attr.inputType)

          if (existingValue !== undefined && existingValue !== null) {
            newAttributeValues[attr.name] = isMulti && typeof existingValue === 'string'
              ? existingValue.split(',').map((v) => v.trim()).filter(Boolean)
              : existingValue
          } else {
            newAttributeValues[attr.name] = isMulti ? [] : null
          }
        })

        this.formData.attributes = { ...newAttributeValues }
      } catch (error) {
        console.error('Failed to fetch attributes', error)
      }
    },
    onSelectProduct (product) {
      if (this.selectedProducts.length === 0) {
        this.formData.name = product.name
      }

      if (this.selectedProducts.some((v) => v.id === product.id)) {
        this.setSnackbar({
          value: true,
          type: 'error',
          message: 'Duplicate product.'
        })
        return
      }

      this.selectedProducts.push(product)
      this.formData.images.image = [...this.formData.images.image, ...product.photoUrls]
    },
    confirmDeleteProduct () {
      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Remove Product',
        message: `Do you want to remove the  ${this.formData.name} ?`,
        confirmText: 'REMOVE',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'CANCEL',
        onConfirm: () => this.deleteProduct()
      })
    },
    async deleteProduct () {
      this.loading = true
      try {
        await LazadaService.deleteProduct(this.editProductId, { brand: this.brandName })
        this.$router.push({ name: 'LazadaProductsList' })
        this.setSnackbar({
          value: true,
          type: 'success',
          message: 'Lazada Product deleted successfully'
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          type: 'error',
          message: `Delete product error msg ${error.message}.`
        })
        console.error('deleteProduct', error)
      } finally {
        this.loading = false
      }
    },
    async onSave () {
      const isValid = await this.$refs['product-form'].validate()

      if (!isValid || this.saving || this.isProductCategoryNotMatch || this.formData.images.image.length > 8) {
        this.setSnackbar({
          value: true,
          type: 'error',
          message: 'Please check the data.'
        })
        return
      }

      try {
        this.loading = true
        if (this.isEditProduct) {
          const payload = {
            ...this.formData,
            skus: {
              sku: this.skus.map((v) => ({
                ...v,
                packageHeight: this.packageHeight,
                packageLength: this.packageLength,
                packageWidth: this.packageWidth,
                packageWeight: this.packageWeight / 1000,
                packageContent: this.packageContent,
                images: {
                  image: Array.isArray(v.images.image)
                    ? v.images.image.map((img) => this.$convertImgix(img))
                    : [this.$convertImgix(v.images.image)].filter(Boolean)
                }
              }))
            },
            brand: this.brandName,
            disableAutoFillAttribute: this.formData.disableAutoFillAttribute || false,
            attributes: Object.keys(this.formData.attributes)
              .map((key) => ({
                name: key,
                value: this.formData.attributes[key]
              }))
              .filter((attribute) => {
                if (Array.isArray(attribute.value)) {
                  return Boolean(attribute.value.length)
                }

                return Boolean(attribute.value)
              }),
            sizeChart: this.formData.sizeChart ? {
              image: {
                url: this.formData.sizeChart,
                uri: this.lazadaData?.sizeChart?.image?.url && this.formData.sizeChart === this.lazadaData.sizeChart.image.url
                  ? this.lazadaData.sizeChart.image.uri : null
              }
            } : null,
            images: {
              image: this.formData.images.image.map((v) => {
                const foundImage = this.lazadaData.images.find((mi) => mi === v)

                return this.$convertImgix(foundImage || v || null)
              })
            }
          }

          await LazadaService.updateProduct(payload)

          this.attributes = []
          this.initialize()

          this.setSnackbar({
            value: true,
            type: 'success',
            message: 'Lazada Product updated successfully'
          })
        } else {
          const payload = {
            ...this.formData,
            skus: {
              sku: this.skus.map((v) => ({
                ...v,
                images: {
                  image: Array.isArray(v.images.image)
                    ? v.images.image.map((img) => this.$convertImgix(img))
                    : [this.$convertImgix(v.images.image)].filter(Boolean)
                },
                packageHeight: this.packageHeight,
                packageLength: this.packageLength,
                packageWidth: this.packageWidth,
                packageWeight: this.packageWeight / 1000,
                packageContent: this.packageContent
              }))
            },
            brand: this.brandName,
            disableAutoFillAttribute: this.formData.disableAutoFillAttribute || false,
            attributes: Object.keys(this.formData.attributes)
              .map((key) => ({
                name: key,
                value: this.formData.attributes[key]
              }))
              .filter((attribute) => {
                if (Array.isArray(attribute.value)) {
                  return Boolean(attribute.value.length)
                }

                return Boolean(attribute.value)
              }),
            sizeChart: this.formData.sizeChart ? {
              image: {
                url: this.formData.sizeChart
              }
            } : null,
            ...(this.sizeChartImage ? { sizeChartImage: this.sizeChartImage } : {}),
            images: {
              image: this.formData.images.image.map((v) => this.$convertImgix(v))
            }
          }

          const { data } = await LazadaService.createProduct(payload)

          this.recommendCategoryId = ''
          this.attributes = []
          this.$router.replace({ name: 'LazadaEditProduct', params: { productId: data.itemId } })
          // this.initialize()

          this.setSnackbar({
            value: true,
            type: 'success',
            message: 'Lazada Product created successfully'
          })

          setTimeout(() => window.location.reload(), 300)
        }
      } catch (error) {
        console.error('onSave', error)
        this.setSnackbar({
          value: true,
          type: 'error',
          message: error.message
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.card-container-error {
  border: 2px dashed red;
}
.text-red {
  color: red !important;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
}
.h-full {
  height: 100%;
}
.text-sm {
  font-size: 12px !important;
}
.product-select-image-wrapper {
  width: 50px;
}
.product-select-wrapper {
  height: 100px;
  width: 100%;
  border-radius: 6px;
  border: 1px solid #cfcfcf;
  padding: 12px;
  display: flex;
}
.cursor-pointer {
  cursor: pointer;
}
.thumbnail-wrapper {
  width: 300px;
  height: 300px;
  border: 1px solid #cfcfcf;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.images-wrapper {
  width: 148px;
  height: 148px;
  border: 1px solid #cfcfcf;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 4px;
  margin-bottom: 4px;
}
.search-product-btn {
  height: 100px;
  width: 100%;
  border-radius: 6px;
  border: 1px dashed #cfcfcf;
  color: #969696;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}
.product-toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.product-toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.product-form {
  max-height: calc(100vh - 160px);
  overflow-y: auto;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
</style>
